import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import PageBreadcrumb from '../components/PageBreadcrumb';
import {
  MagnifyingGlassIcon,
  HeartIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StarIcon as StarOutline,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { StarIcon, HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { promptsAPI, featuredProjectsAPI } from '../services/api';
import AdSenseAd from '../components/AdSenseAd';
import FeaturedProjectAdCard from '../components/FeaturedProjectAdCard';
import { useAuth } from '../contexts/AuthContext';
import { Prompt, PromptCategory, AITool } from '../types';
import toast from 'react-hot-toast';

const PAGE_BG = { background: 'var(--base)', minHeight: '100vh' };

/* ─── Stat Pill ────────────────────────────────────────────────── */
const StatPill: React.FC<{ value: string | number; label: string; color?: string }> = ({ value, label, color = 'var(--signal)' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
    <span style={{
      fontFamily: 'Plus Jakarta Sans, sans-serif',
      fontSize: 20,
      fontWeight: 800,
      letterSpacing: '-0.03em',
      color,
      lineHeight: 1,
    }}>{value}</span>
    <span style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 9,
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'var(--text-secondary)',
      opacity: 0.6,
    }}>{label}</span>
  </div>
);

/* ─── AI Tool badge helper ─────────────────────────────────────── */
const toolBadgeClass = (tool: string): string => {
  const t = tool.toLowerCase();
  if (t.includes('chatgpt') || t.includes('gpt'))  return 'tool-badge tool-badge-chatgpt';
  if (t.includes('claude'))                         return 'tool-badge tool-badge-claude';
  if (t.includes('midjourney'))                     return 'tool-badge tool-badge-midjourney';
  if (t.includes('dall'))                           return 'tool-badge tool-badge-dalle';
  if (t.includes('stable') || t.includes('sd'))     return 'tool-badge tool-badge-sd';
  if (t.includes('copilot') || t.includes('github')) return 'tool-badge tool-badge-copilot';
  return 'tool-badge tool-badge-default';
};

/* ─── Select ───────────────────────────────────────────────────── */
const Select: React.FC<{
  value: string;
  onChange: (v: string) => void;
  label: string;
  options: { value: string; label: string }[];
  id?: string;
}> = ({ value, onChange, label, options, id }) => {
  const inputId = id || `select-${label.replace(/\s/g, '-').toLowerCase()}`;
  return (
    <div>
      <label htmlFor={inputId} style={{ display: 'block', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6, fontFamily: 'JetBrains Mono, monospace' }}>
        {label}
      </label>
      <select
        id={inputId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input"
        style={{ appearance: 'none', cursor: 'pointer', fontSize: 13, minHeight: 44 }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ background: 'var(--surface-1)', color: 'var(--text-primary)' }}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

/* ─── Prompt Card ──────────────────────────────────────────────── */
const PromptCard: React.FC<{
  prompt: Prompt;
  isFavorited: boolean;
  onFavorite: (id: string) => void;
  delay?: number;
}> = ({ prompt, isFavorited, onFavorite, delay = 0 }) => {
  const author = typeof prompt.author === 'object' ? prompt.author : null;
  const snippet = prompt.prompt?.slice(0, 380) || prompt.description?.slice(0, 380) || '';
  const [hovered, setHovered] = React.useState(false);
  const hasImage = prompt.images && prompt.images.length > 0;

  return (
    <div
      className="card animate-fade-up flex flex-col h-full"
      style={{
        padding: 0,
        animationDelay: `${delay}ms`,
        borderLeft: '3px solid transparent',
        transition: 'border-left-color 200ms ease, box-shadow 220ms ease, transform 220ms cubic-bezier(0.22,1,0.36,1), border-color 220ms ease',
        overflow: 'hidden',
        ...(hovered ? { borderLeftColor: 'var(--signal)' } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image preview if available */}
      {hasImage && (
        <div style={{
          width: '100%',
          height: 180,
          backgroundColor: 'var(--surface-2)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
          <img
            src={prompt.images![0]}
            alt={prompt.title}
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 300ms ease',
              transform: hovered ? 'scale(1.05)' : 'scale(1)',
            }}
          />
        </div>
      )}
      <div style={{ padding: '14px 18px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Top row: Tool badge + NSFW + favorite */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={toolBadgeClass(prompt.aiTool)}>{prompt.aiTool}</span>
            {prompt.isNSFW && (
              <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: '#ef4444', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 3, padding: '1px 5px', letterSpacing: '0.06em' }}>NSFW</span>
            )}
          </div>
          <button
            onClick={(e) => { e.preventDefault(); onFavorite(prompt._id); }}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 4, borderRadius: 6,
              color: isFavorited ? 'var(--destructive)' : 'var(--text-secondary)',
              opacity: isFavorited ? 1 : 0.45,
              transition: 'opacity 150ms, color 150ms, background 150ms',
              minWidth: 28, minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--destructive)'; (e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.08)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = isFavorited ? '1' : '0.45'; (e.currentTarget as HTMLButtonElement).style.color = isFavorited ? 'var(--destructive)' : 'var(--text-secondary)'; (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
          >
            {isFavorited ? <HeartSolid style={{ width: 14, height: 14 }} /> : <HeartIcon style={{ width: 14, height: 14 }} />}
          </button>
        </div>

        {/* Category chip */}
        <div>
          <span className="chip" style={{ fontSize: 10 }}>{prompt.category.replace(/-/g, ' ')}</span>
        </div>

        {/* Title */}
        <Link to={`/prompts/${prompt._id}`}>
          <h3
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: 14,
              fontWeight: 700,
              color: hovered ? 'var(--signal)' : 'var(--text-primary)',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              margin: 0,
              transition: 'color 150ms',
            }}
            className="line-clamp-1"
          >
            {prompt.title}
          </h3>
        </Link>

        {snippet && (
          <div style={{
            fontSize: 12.5,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            flex: 1,
            marginBottom: 0,
          }}>
            {snippet}{snippet.length >= 380 && <span style={{ opacity: 0.4 }}>…</span>}
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1" title={`Rating: ${prompt.ratings.average.toFixed(1)}`}>
            <StarIcon style={{ width: 12, height: 12, color: 'var(--signal)' }} />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{prompt.ratings.average.toFixed(1)}</span>
          </div>
          <div className="flex items-center gap-1">
            <EyeIcon style={{ width: 12, height: 12, color: 'var(--text-secondary)', opacity: 0.4 }} aria-hidden="true" />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', opacity: 0.5, fontFamily: 'JetBrains Mono, monospace' }}>{prompt.views.toLocaleString()}</span>
          </div>
        </div>
        {author && (
          <Link to={`/profile/${author.username}`} className="flex items-center gap-1.5" style={{ transition: 'opacity 150ms' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = '0.8')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = '1')}
          >
            <img
              src={author.avatar || `https://ui-avatars.com/api/?name=${author.username}&background=E8EDF8&color=3D4F6B&size=32`}
              alt={author.username}
              className="avatar"
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 10, color: 'var(--text-secondary)', opacity: 0.65, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{author.username}</span>
          </Link>
        )}
      </div>
    </div>
  );
};

/* ─── PromptsPage ──────────────────────────────────────────────── */
const PromptsPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });
  const [favoriteStatus, setFavoriteStatus] = useState<Record<string, boolean>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [featuredProjects, setFeaturedProjects] = useState<any[]>([]);

  const categories = [
    { value: '', label: t('promptsPage.allCategories') },
    { value: 'text-generation', label: 'Writing & Chat' },
    { value: 'image-generation', label: 'Art & Design' },
    { value: 'code-generation', label: 'Coding' },
    { value: 'audio-generation', label: 'Music & Audio' },
    { value: 'video-generation', label: 'Video & Motion' },
    { value: 'data-analysis', label: 'Data Science' },
    { value: 'translation', label: 'Translation' },
    { value: 'other', label: 'Other' },
  ];

  const aiTools = [
    { value: '', label: t('promptsPage.allTools') },
    { value: 'ChatGPT', label: 'ChatGPT' },
    { value: 'Claude', label: 'Claude' },
    { value: 'Midjourney', label: 'Midjourney' },
    { value: 'DALL-E', label: 'DALL-E' },
    { value: 'Stable Diffusion', label: 'Stable Diffusion' },
    { value: 'GitHub Copilot', label: 'Copilot' },
    { value: 'CodeLlama', label: 'CodeLlama' },
    { value: 'Other', label: 'Other' },
  ];

  const sortOptions = [
    { value: 'newest', label: t('promptsPage.newest') },
    { value: 'rating', label: t('promptsPage.bestRated') },
    { value: 'favorites', label: t('promptsPage.mostSaved') },
    { value: 'views', label: t('promptsPage.mostViewed') },
  ];

  const filters = {
    search: searchParams.get('search') || '',
    category: searchParams.get('category') as PromptCategory || undefined,
    aiTool: searchParams.get('aiTool') as AITool || undefined,
    sort: (searchParams.get('sort') as any) || 'newest',
    featured: searchParams.get('featured') === 'true',
    page: parseInt(searchParams.get('page') || '1'),
  };

  useEffect(() => { load(); }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    featuredProjectsAPI.getRandomProjects(3).then(res => setFeaturedProjects(res.projects || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await promptsAPI.getPrompts(filters);
      setPrompts(res.prompts || []);
      setPagination(res.pagination);
    } catch { toast.error(t('prompts.failedToLoad')); }
    finally { setLoading(false); }
  };

  const update = (newFilters: Partial<typeof filters>) => {
    const merged = { ...filters, ...newFilters };
    const params = new URLSearchParams();
    Object.entries(merged).forEach(([k, v]) => {
      if (v !== undefined && v !== '' && v !== 'newest' && v !== 1 && v !== false)
        params.set(k, String(v));
    });
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = (new FormData(e.currentTarget).get('search') as string) || '';
    update({ search: q, page: 1 });
  };

  const handleFavorite = async (id: string) => {
    if (!user) { toast.error(t('prompts.loginToSave')); return; }
    try {
      const res = await promptsAPI.toggleFavorite(id);
      setFavoriteStatus((prev) => ({ ...prev, [id]: res.favorited }));
      setPrompts((prev) => prev.map((p) => p._id === id ? { ...p, favorites: res.favoritesCount } : p));
    } catch { toast.error(t('errors.somethingWentWrong')); }
  };

  const isFav = (id: string) => favoriteStatus[id] || user?.favorites.includes(id) || false;

  const hasFilters = filters.category || filters.aiTool || filters.featured;

  return (
    <div style={PAGE_BG}>
      <SEO
        title="Prompts de IA - DevPromptly"
        description="Explora miles de prompts de IA para ChatGPT, Claude y más herramientas"
        canonical="https://axioma.javzdev.com/prompts"
      />

      {/* ─── Hero strip ─────────────────────────────────────────── */}
      <div style={{ borderBottom: '1px solid var(--border-default)', position: 'relative', overflow: 'hidden' }}>
        {/* Ambient glows */}
        <div style={{ position: 'absolute', top: '-60%', right: '-5%', width: '35%', height: '200%', background: 'radial-gradient(ellipse, rgba(79,70,229,0.07) 0%, transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40%', left: '10%', width: '25%', height: '150%', background: 'radial-gradient(ellipse, rgba(59,130,246,0.05) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div className="max-w-7xl mx-auto px-5 sm:px-8" style={{ paddingTop: 28, paddingBottom: 24, position: 'relative', zIndex: 1 }}>
          <PageBreadcrumb breadcrumbs={[{ label: 'Explorar', path: '/prompts' }]} />

          {/* Title row */}
          <div style={{ marginBottom: 20 }}>
            <span className="mono-label" style={{ marginBottom: 6 }}>{t('promptsPage.library')}</span>
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 'clamp(22px,3vw,32px)', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 6 }}>
              {t('promptsPage.explorePrompts')}
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', opacity: 0.75 }}>
              Descubre, guarda y comparte los mejores prompts para cada herramienta de IA.
            </p>
          </div>

          {/* Search — full-width prominent bar */}
          <div style={{ marginBottom: 20 }}>
            <form onSubmit={handleSearch} role="search" aria-label="Search prompts">
              <div className="search-wrapper" style={{ maxWidth: 640 }}>
                <div className="search-glow-ring" aria-hidden="true" />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <MagnifyingGlassIcon
                    aria-hidden="true"
                    style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: 'var(--text-secondary)', opacity: 0.4, pointerEvents: 'none' }}
                  />
                  <input
                    name="search"
                    type="search"
                    defaultValue={filters.search}
                    placeholder="Busca por titulo, herramienta o categoria... (ej. ChatGPT copywriting)"
                    aria-label="Search prompts"
                    className="search-prominent"
                    style={{ paddingLeft: 44, paddingRight: 120 }}
                  />
                  <button
                    type="submit"
                    className="btn btn-signal"
                    style={{ position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)', padding: '7px 16px', fontSize: 13, height: 36, gap: 6, minHeight: 'unset' }}
                  >
                    <MagnifyingGlassIcon style={{ width: 13, height: 13 }} aria-hidden="true" />
                    Buscar
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Stats strip + filter toggle */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-6">
              <StatPill value={pagination.total > 0 ? `${pagination.total}+` : '...'} label="prompts" />
              <div style={{ width: 1, height: 28, background: 'var(--border-default)' }} />
              <StatPill value="8+" label="categorías" color="#3b82f6" />
              <div style={{ width: 1, height: 28, background: 'var(--border-default)' }} />
              <StatPill value="9" label="herramientas" color="var(--cyan)" />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn ${showFilters ? 'btn-signal' : 'btn-ghost'}`}
              style={{ padding: '8px 14px', gap: 6, fontSize: 12 }}
            >
              <AdjustmentsHorizontalIcon style={{ width: 14, height: 14 }} />
              {t('promptsPage.filters')}
              {hasFilters && <span style={{ width: 6, height: 6, borderRadius: '50%', background: showFilters ? '#fff' : 'var(--signal)', display: 'inline-block' }} />}
            </button>
          </div>
        </div>
      </div>

      {/* Filters panel */}
      <div style={{ borderBottom: showFilters ? '1px solid var(--border-default)' : 'none' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {/* Inline filter panel */}
          {showFilters && (
            <div
              className="animate-fade-up"
              style={{ paddingTop: 16, paddingBottom: 16 }}
              role="region"
              aria-label="Filtros de búsqueda"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Select
                  label={t('promptsPage.category')}
                  id="filter-category"
                  value={filters.category || ''}
                  onChange={(v) => update({ category: (v as PromptCategory) || undefined, page: 1 })}
                  options={categories}
                />
                <Select
                  label={t('promptsPage.aiTool')}
                  id="filter-tool"
                  value={filters.aiTool || ''}
                  onChange={(v) => update({ aiTool: (v as AITool) || undefined, page: 1 })}
                  options={aiTools}
                />
                <Select
                  label={t('promptsPage.sort')}
                  id="filter-sort"
                  value={filters.sort}
                  onChange={(v) => update({ sort: v as any, page: 1 })}
                  options={sortOptions}
                />
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    onClick={() => update({ featured: !filters.featured, page: 1 })}
                    className={`btn ${filters.featured ? 'btn-signal' : 'btn-ghost'}`}
                    aria-pressed={filters.featured}
                    style={{ fontSize: 13, padding: '10px 14px', gap: 6, minHeight: 44 }}
                  >
                    <StarOutline style={{ width: 14, height: 14 }} aria-hidden="true" />
                    {filters.featured ? `${t('promptsPage.featuredOnly')} ✓` : t('promptsPage.featuredOnly')}
                  </button>
                  {hasFilters && (
                    <button
                      onClick={() => setSearchParams({})}
                      className="btn btn-ghost"
                      style={{ fontSize: 12, padding: '8px 12px', color: 'var(--text-secondary)', opacity: 0.8, minHeight: 40 }}
                    >
                      {t('promptsPage.clearAll')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8" style={{ paddingTop: 32, paddingBottom: 72 }}>
        {/* Results meta */}
        <div className="flex items-center justify-between mb-6">
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', opacity: 0.7 }}>
            {loading ? t('promptsPage.loading') : t('promptsPage.results', { count: pagination.total })}
          </p>
          {!loading && pagination.pages > 1 && (
            <div className="flex items-center gap-2">
              <button
                disabled={pagination.current === 1}
                onClick={() => update({ page: pagination.current - 1 })}
                className="btn btn-ghost"
                style={{ padding: '6px 10px', opacity: pagination.current === 1 ? 0.3 : 1 }}
              >
                <ChevronLeftIcon style={{ width: 14, height: 14 }} />
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}>
                {pagination.current} / {pagination.pages}
              </span>
              <button
                disabled={pagination.current === pagination.pages}
                onClick={() => update({ page: pagination.current + 1 })}
                className="btn btn-ghost"
                style={{ padding: '6px 10px', opacity: pagination.current === pagination.pages ? 0.3 : 1 }}
              >
                <ChevronRightIcon style={{ width: 14, height: 14 }} />
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center" style={{ paddingTop: 80, gap: 16 }}>
            <div className="spinner" />
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--text-secondary)', opacity: 0.5, letterSpacing: '0.12em' }}>{t('promptsPage.loading').toUpperCase()}</span>
          </div>
        ) : prompts.length > 0 ? (
          <>
            {/* Featured Project Ad — every 4 items, integrated into grid */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {prompts.map((p, i) => {
                const showFeaturedAd = (i + 1) % 4 === 0 && i > 0 && featuredProjects.length > 0;

                return (
                  <React.Fragment key={p._id}>
                    {/* If showing ad at this position, insert it above the grid */}
                    {showFeaturedAd && i === 3 && (
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', margin: '24px 0' }}>
                        {(() => {
                          const filtered = featuredProjects.filter((proj) => proj.placements?.explorePrompts ?? true);
                          return filtered.length > 0 ? (
                            <FeaturedProjectAdCard project={filtered[Math.floor(Math.random() * filtered.length)]} variant="compact" />
                          ) : null;
                        })()}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {prompts.map((p, i) => (
                <PromptCard
                  key={p._id}
                  prompt={p}
                  isFavorited={isFav(p._id)}
                  onFavorite={handleFavorite}
                  delay={i * 40}
                />
              ))}
            </div>

            {/* Ad — after grid, before pagination */}
            <div style={{ marginTop: 40, marginBottom: 8 }}>
              <AdSenseAd adSlot="7259836401" adFormat="horizontal" />
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center gap-1 mt-12">
                <button
                  disabled={pagination.current === 1}
                  onClick={() => update({ page: pagination.current - 1 })}
                  className="btn btn-ghost"
                  style={{ fontSize: 12, opacity: pagination.current === 1 ? 0.3 : 1 }}
                >
                  {t('promptsPage.previous')}
                </button>
                {[...Array(Math.min(pagination.pages, 7))].map((_, i) => {
                  const page = i + 1;
                  const active = pagination.current === page;
                  return (
                    <button
                      key={page}
                      onClick={() => update({ page })}
                      className="btn"
                      style={{
                        width: 36, height: 36, padding: 0, fontSize: 12,
                        background: active ? 'var(--signal)' : 'transparent',
                        color: active ? 'var(--base)' : 'var(--text-secondary)',
                        borderColor: active ? 'var(--signal)' : 'var(--border-default)',
                        fontWeight: active ? 700 : 400,
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  disabled={pagination.current === pagination.pages}
                  onClick={() => update({ page: pagination.current + 1 })}
                  className="btn btn-ghost"
                  style={{ fontSize: 12, opacity: pagination.current === pagination.pages ? 0.3 : 1 }}
                >
                  {t('promptsPage.next')}
                </button>
              </div>
            )}
          </>
        ) : (
          <div
            className="flex flex-col items-center justify-center text-center"
            style={{ paddingTop: 80, paddingBottom: 80 }}
          >
            <MagnifyingGlassIcon style={{ width: 32, height: 32, color: 'var(--text-secondary)', opacity: 0.2, marginBottom: 16 }} />
            <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
              {t('promptsPage.noPromptsFound')}
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 300, marginBottom: 24 }}>
              {t('promptsPage.adjustFilters')}
            </p>
            <button onClick={() => setSearchParams({})} className="btn btn-ghost">
              {t('promptsPage.resetFilters')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptsPage;
